/**
 * Single source of display formatting — dates, hours (#739).
 *
 * `utils/date-utils.ts` stays responsible for the computation (parsing, arithmetic, week bounds);
 * this module only turns an already computed value into on-screen text. After this work, no call
 * to `toLocaleDateString`, `Intl.*` or a display `toFixed` should exist anywhere else in `src/` —
 * if any are left, they have a local reason documented where they are.
 *
 * Locale: the one of the current language (`getLocaleTag()`, `fr-CA` by default), read at each call so a
 * language switch applies at once. The tests run in French, where the rendered text is the historical one.
 *
 * Time-zone note (#741): the `Date`s received here are local dates built by
 * `parseDateStringAsLocal` / `combineDateAndTime` — a pure calendar (year/month/day), not a UTC
 * instant. Reading them (`getFullYear`/`getMonth`/`getDate`, used internally by
 * `toLocaleDateString`) therefore happens in the same local clock as the one that built them.
 * Pinning `timeZone: 'America/Toronto'` on the calls below would break that consistency on a
 * machine set to another time zone (the calendar read would no longer be the one that was built) —
 * exactly the bug of #741, moved from writing to reading. That is why this file deliberately sets
 * no time zone on its `Intl`/`toLocaleDateString` calls: the time zone of Parcours users
 * (`America/Toronto`, exported below for reference) is already the one in which those `Date`s were
 * made. A genuine server instant (a timestamp, not a calendar date) is the opposite case and goes
 * through `formatInstant`, the one function here that does pin the zone.
 */

import { parseDateStringAsLocal } from '@/utils/date-utils';
import { roundHours } from '@/components/presence-hours';
import { getLocaleTag, translate } from '@fondationcss/sdk';

/** The locale of the current language, read at each call (the language can change while the application runs). */
const currentLocale = (): string => getLocaleTag();

/** The time zone of Parcours users — see the time-zone note above for why it is not passed to
 *  this file's `Intl`/`toLocaleDateString` calls. */
export const TIME_ZONE = 'America/Toronto';

/**
 * Formats a date string in the given locale (moved from `utils/date-utils.ts:formatDateString`,
 * #739 — behaviour unchanged, 8 callers).
 * @param dateString - Date string to format
 * @param locale - Locale to use (default: 'fr-CA')
 * @returns Formatted date string, or '' when `dateString` is empty
 */
export function formatDateString(dateString: string | undefined, locale: string = currentLocale()): string {
  if (!dateString) return '';
  const date = parseDateStringAsLocal(dateString);
  return date.toLocaleDateString(locale);
}

/** `31/12/2025`-like default rendering of `toLocaleDateString('fr-CA')` with no options — « 2025-12-31 ». */
export function formatDateNumeric(date: Date): string {
  return date.toLocaleDateString(currentLocale());
}

/** Default rendering in a grid: « 31 déc. 2025 ». */
export function formatDateShort(date: Date): string {
  return date.toLocaleDateString(currentLocale(), { day: 'numeric', month: 'short', year: 'numeric' });
}

/** « 31 décembre 2025 ». */
export function formatDateLong(date: Date): string {
  return date.toLocaleDateString(currentLocale(), { day: 'numeric', month: 'long', year: 'numeric' });
}

/** « mercredi 31 décembre 2025 ». */
export function formatDateWeekday(date: Date): string {
  return date.toLocaleDateString(currentLocale(), { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

/** « mercredi 31 décembre » — like `formatDateWeekday`, without the year. */
export function formatDateWeekdayShort(date: Date): string {
  return date.toLocaleDateString(currentLocale(), { weekday: 'long', day: 'numeric', month: 'long' });
}

/** « mercredi » seul. */
export function formatWeekdayLong(date: Date): string {
  return date.toLocaleDateString(currentLocale(), { weekday: 'long' });
}

/** « 31 déc. » — day + abbreviated month, no year. */
export function formatDateDayMonthShort(date: Date): string {
  return date.toLocaleDateString(currentLocale(), { day: 'numeric', month: 'short' });
}

/** « 31 décembre » — day + full month, no year. */
export function formatDayMonthLong(date: Date): string {
  return date.toLocaleDateString(currentLocale(), { day: 'numeric', month: 'long' });
}

/** « 31 déc. – 14 janv. 2026 » — abbreviated start with no year, abbreviated end with year. */
export function formatDateRange(start: Date, end: Date): string {
  return `${formatDateDayMonthShort(start)} – ${formatDateShort(end)}`;
}

/** « 31 déc. – 14 janv. » — abbreviated start and end, neither showing the year. */
export function formatDateRangeShort(start: Date, end: Date): string {
  return `${formatDateDayMonthShort(start)} – ${formatDateDayMonthShort(end)}`;
}

/** « 05 » — day of the month on two digits. */
export function formatDayOfMonth(date: Date): string {
  return date.toLocaleDateString(currentLocale(), { day: '2-digit' });
}

/** « DÉC » — abbreviated month, full stop removed, upper-cased. */
export function formatMonthAbbrUpper(date: Date): string {
  return date.toLocaleDateString(currentLocale(), { month: 'short' }).replace('.', '').toUpperCase();
}

/**
 * « 23 sept. 2026, 08 h 22 » — a genuine server INSTANT (an ISO timestamp, `Z` or offset), read
 * in the school's time zone (#989: when a GRICS import ran).
 *
 * The one place this file pins `timeZone`, and on purpose: the note at the top is about calendar
 * dates built locally, which pinning would shift. An instant is the opposite case — it has no
 * calendar of its own until a zone is chosen, and the school's is the one the reader means.
 * A timestamp without a zone is taken as UTC, which is how the Parcours API sends them.
 */
export function formatInstant(iso: string): string {
  const instant = new Date(/[zZ]|[+-]\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`);
  return instant.toLocaleString(currentLocale(), {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: TIME_ZONE,
  });
}

/** « A, B et C » in French, « A, B, and C » in English: a list joined the way the language in force writes it. */
export function formatList(items: readonly string[]): string {
  if (items.length <= 1) return items.join('');
  if (items.length === 2) return translate('parcours.common.listPair', { a: items[0], b: items[1] });
  return translate('parcours.common.listMany', { head: items.slice(0, -1).join(', '), last: items[items.length - 1] });
}

/** « 1 400 » — a count, grouped the French way (#989: the GRICS imports' progress and figures). */
export function formatCount(n: number): string {
  return new Intl.NumberFormat(currentLocale(), { maximumFractionDigits: 0 }).format(n);
}

/**
 * Formats a duration in hours with `Intl.NumberFormat`, followed by « h » (#739 — two copies,
 * `work-placements.vue` / `my-work-placement.vue`, differing only in the number of decimals).
 */
export function formatHoursIntl(hours: number, maximumFractionDigits: number): string {
  return `${new Intl.NumberFormat(currentLocale(), { maximumFractionDigits }).format(hours)} h`;
}

/**
 * Formats a duration the French way: 7.5 → « 7,50 » (moved from `components/presence-hours.ts`,
 * #739 — this is the reference tested implementation, body unchanged).
 */
export function formatHours(hours: number): string {
  return roundHours(hours).toFixed(2).replace('.', ',');
}

/**
 * Formats a duration the French way, without trailing zeros: 22.5 → « 22,5 », 45 → « 45 ».
 *
 * `formatHours` renders two decimals always, which is right for effective hours (7,50 h read off a
 * period) but wrong for a competency's duration, where « 45,00 h » is noise — those figures are
 * whole or half hours. Like `formatHours`, it returns the number alone: the caller appends « h ».
 * Added by #1002, which replaced the local `fmtHours` of `module-sequence-list.vue` (a bare
 * `toString`, so « 22.5 » with an English decimal point).
 */
export function formatHoursCompact(hours: number): string {
  return new Intl.NumberFormat(currentLocale(), { maximumFractionDigits: 2 }).format(roundHours(hours));
}

/** French duration label with unit: 150 → « 2,5 h », 180 → « 3 h ». */
export function formatDurationHours(durationMinutes: number): string {
  // i18n-ignore: Unicode escaping preserves the existing numeric duration label.
  return `${formatHoursCompact(durationMinutes / 60)}\u00a0h`;
}

/**
 * The time of day a teacher reads on the next-assignment card: « 14h30 » (« 14h » on the hour) in
 * French, « 2:30 p.m. » in English — it follows the language in use, not a fixed locale.
 */
export function formatClockTime(date: Date): string {
  const h = date.getHours();
  const m = date.getMinutes();
  if (getLocaleTag() === 'en-CA') {
    return date.toLocaleTimeString('en-CA', m === 0 ? { hour: 'numeric' } : { hour: 'numeric', minute: '2-digit' });
  }
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`;
}
